import { QueryClientProvider } from '@tanstack/react-query';
import { ConfirmProvider, createQueryClient, EmptyState, FullPageSpinner, ToastProvider } from '@wl/ui';
import { type ComponentType, lazy, Suspense } from 'react';
import { createBrowserRouter, Link, Navigate, Outlet, RouterProvider, useLocation } from 'react-router';
import { AuthProvider, useAuth } from '../lib/auth';
import { AdminThemeProvider } from '../lib/theme';
import { AdminLayout } from './AdminLayout';
import { MODULES } from './modules';

const queryClient = createQueryClient();
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const SetupPage = lazy(() => import('../pages/auth/SetupPage'));
const AcceptInvitePage = lazy(() => import('../pages/auth/AcceptInvitePage'));
const PAGES = new Map(MODULES.map((m) => [m.path, lazy(m.load)]));

function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous') {
    const next = location.pathname + location.search;
    return <Navigate to={`/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />;
  }
  return <AdminLayout />;
}

function GuestOnly() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'authenticated') {
    const next = new URLSearchParams(location.search).get('next');
    return <Navigate to={next?.startsWith('/') && !next.startsWith('//') ? next : '/'} replace />;
  }
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <LoginPage />
    </Suspense>
  );
}

/** Oturumdan bağımsız sayfalar (kurulum, davet); yönetim düzeninin dışında. */
function Public({ page: Page }: { page: ComponentType }) {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Page />
    </Suspense>
  );
}

export function NotFound() {
  return (
    <EmptyState
      icon="alert"
      title="Sayfa bulunamadı"
      action={
        <Link to="/" className="text-sm font-medium text-primary underline">
          Genel bakışa dön
        </Link>
      }
    />
  );
}

function Root() {
  return (
    <AdminThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <ConfirmProvider>
            <Outlet />
          </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </AdminThemeProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      { path: '/login', element: <GuestOnly /> },
      { path: '/setup', element: <Public page={SetupPage} /> },
      { path: '/invite', element: <Public page={AcceptInvitePage} /> },
      {
        element: <RequireAuth />,
        children: [
          ...MODULES.map((m) => {
            const Page = PAGES.get(m.path)!;
            return {
              path: m.path,
              element: (
                <Suspense fallback={<div className="py-10 text-center text-muted">Yükleniyor…</div>}>
                  <Page />
                </Suspense>
              ),
            };
          }),
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
